import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { logger } from '@/services/loggerService';
import { errorReporter } from '@/services/errorReportingService';

const WAVEFORM_BAR_COUNT = 40;
const WAVEFORM_UPDATE_MS = 100;
const CHUNK_INTERVAL = 1000;

const isTypeSupportedSafe = (type: string): boolean => {
  try {
    return typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type);
  } catch {
    return false;
  }
};

const pickAudioMime = (): { mime: string; ext: string } => {
  if (typeof window === 'undefined' || typeof MediaRecorder === 'undefined') {
    return { mime: 'audio/webm;codecs=opus', ext: 'webm' };
  }

  const candidates: Array<{ mime: string; ext: string }> = [
    { mime: 'audio/webm;codecs=opus', ext: 'webm' },
    { mime: 'audio/webm', ext: 'webm' },
    { mime: 'audio/ogg;codecs=opus', ext: 'ogg' },
    { mime: 'audio/mp4', ext: 'mp4' },
  ];

  for (const candidate of candidates) {
    if (isTypeSupportedSafe(candidate.mime)) {
      return candidate;
    }
  }

  return { mime: 'audio/webm', ext: 'webm' };
};

interface UseAudioRecorderOptions {
  maxDuration?: number;
  audioDeviceId?: string;
  onRecordingComplete?: (file: File, durationSeconds: number) => void;
  onStaleMic?: () => void;
}

interface UseAudioRecorderReturn {
  isRecording: boolean;
  isPaused: boolean;
  isPreviewing: boolean;
  elapsed: number;
  audioLevels: number[];
  analyserRef: React.RefObject<AnalyserNode | null>;
  error: string | null;
  startPreview: (deviceIdOverride?: string) => Promise<boolean>;
  stopPreview: () => void;
  startRecording: () => Promise<void>;
  pauseRecording: () => void;
  resumeRecording: () => void;
  stopRecording: () => void;
  resetRecording: () => void;
  clearError: () => void;
}

export function useAudioRecorder(options: UseAudioRecorderOptions = {}): UseAudioRecorderReturn {
  const { maxDuration = 120, audioDeviceId, onRecordingComplete, onStaleMic } = options;

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const analyserIntervalRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const elapsedRef = useRef(0);
  const recordingStartTimeRef = useRef<number | null>(null);
  const pauseStartTimeRef = useRef<number | null>(null);
  const pausedDurationRef = useRef(0);

  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [audioLevels, setAudioLevels] = useState<number[]>(() => new Array(WAVEFORM_BAR_COUNT).fill(0.05));
  const [error, setError] = useState<string | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const previewDeviceIdRef = useRef<string | undefined>(undefined);
  const previewStartingRef = useRef(false);
  const previewPromiseRef = useRef<Promise<boolean> | null>(null);
  /** Guards against `getUserMedia` resolving after unmount and orphaning a live mic. */
  const isUnmountedRef = useRef(false);

  const mimeInfo = useMemo(() => pickAudioMime(), []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const clearAnalyser = useCallback(() => {
    if (analyserIntervalRef.current) {
      window.clearInterval(analyserIntervalRef.current);
      analyserIntervalRef.current = null;
    }
  }, []);

  const cleanupStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
      analyserRef.current = null;
    }
  }, []);

  const startWaveformUpdates = useCallback(() => {
    clearAnalyser();
    analyserIntervalRef.current = window.setInterval(() => {
      const analyser = analyserRef.current;
      if (!analyser) return;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteFrequencyData(dataArray);

      const sum = dataArray.reduce((acc, val) => acc + val, 0);
      const average = sum / dataArray.length / 255;
      const normalized = Math.max(0.05, average);

      setAudioLevels((prev) => [...prev.slice(1), normalized]);
    }, WAVEFORM_UPDATE_MS);
  }, [clearAnalyser]);

  const startTimer = useCallback(() => {
    clearTimer();
    timerRef.current = window.setInterval(() => {
      elapsedRef.current += 1;
      const current = elapsedRef.current;
      if (current >= maxDuration) {
        setElapsed(maxDuration);
        if (recorderRef.current && recorderRef.current.state === 'recording') {
          recorderRef.current.stop();
        }
      } else {
        setElapsed(current);
      }
    }, 1000);
  }, [clearTimer, maxDuration]);

  const handleStop = useCallback(() => {
    clearTimer();
    clearAnalyser();
    setIsRecording(false);
    setIsPaused(false);

    const startedAt = recordingStartTimeRef.current;
    const pauseStartedAt = pauseStartTimeRef.current;
    const pausedMs = pausedDurationRef.current;
    recordingStartTimeRef.current = null;
    pauseStartTimeRef.current = null;
    pausedDurationRef.current = 0;
    recorderRef.current = null;

    const { mime, ext } = mimeInfo;
    const baseType = mime.split(';')[0];

    if (chunksRef.current.length === 0) {
      setError('No audio data was recorded. Please try again.');
      cleanupStream();
      return;
    }

    const blob = new Blob(chunksRef.current, { type: baseType });
    chunksRef.current = [];

    if (blob.size === 0) {
      setError('Recorded audio was empty. Please try again.');
      cleanupStream();
      return;
    }

    const measuredDuration = (() => {
      if (typeof startedAt === 'number') {
        const pendingPauseMs = pauseStartedAt ? Date.now() - pauseStartedAt : 0;
        const totalPausedMs = pausedMs + Math.max(pendingPauseMs, 0);
        return Math.max(Date.now() - startedAt - totalPausedMs, 0) / 1000;
      }
      return 0;
    })();

    const duration = Math.max(measuredDuration, elapsedRef.current, 0.1);
    const safeDuration = Math.min(duration, maxDuration);

    const fileName = `audio-${Date.now()}.${ext}`;
    const file = new File([blob], fileName, { type: baseType });

    cleanupStream();
    onRecordingComplete?.(file, safeDuration);
  }, [clearTimer, clearAnalyser, mimeInfo, maxDuration, cleanupStream, onRecordingComplete]);

  const handleDataAvailable = useCallback((event: BlobEvent) => {
    if (event.data && event.data.size > 0) {
      chunksRef.current.push(event.data);
    }
  }, []);

  const setupAudioPipeline = useCallback((stream: MediaStream) => {
    streamRef.current = stream;
    const audioContext = new AudioContext();
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);
    audioContextRef.current = audioContext;
    analyserRef.current = analyser;
  }, []);

  const setupAndStartRecording = useCallback(
    (stream: MediaStream) => {
      // Only set up audio pipeline if not already done (e.g., by preview)
      if (!audioContextRef.current || !analyserRef.current) {
        setupAudioPipeline(stream);
      } else {
        streamRef.current = stream;
      }

      const { mime } = mimeInfo;
      let recorder: MediaRecorder;
      try {
        recorder = new MediaRecorder(stream, {
          mimeType: mime,
          audioBitsPerSecond: 128_000,
        });
      } catch {
        logger.warn('audio', 'Primary audio MIME failed, using default');
        recorder = new MediaRecorder(stream);
      }

      recorderRef.current = recorder;
      recorder.ondataavailable = handleDataAvailable;
      recorder.onstop = handleStop;

      recordingStartTimeRef.current = Date.now();
      pauseStartTimeRef.current = null;
      pausedDurationRef.current = 0;

      recorder.start(CHUNK_INTERVAL);
      setIsRecording(true);
      setIsPreviewing(false);
      startTimer();
      startWaveformUpdates();
    },
    [setupAudioPipeline, mimeInfo, handleDataAvailable, handleStop, startTimer, startWaveformUpdates],
  );

  const startPreview = useCallback(
    async (deviceIdOverride?: string): Promise<boolean> => {
      // Already recording — preview not applicable
      if (recorderRef.current) return false;

      // Deduplicate: if a preview is already in-flight, return the same promise
      // so callers (e.g., handleStart) wait for it instead of treating "busy" as failure
      if (previewStartingRef.current && previewPromiseRef.current) {
        return previewPromiseRef.current;
      }

      previewStartingRef.current = true;

      const promise = (async (): Promise<boolean> => {
        // Clean up any existing preview
        if (streamRef.current) {
          cleanupStream();
          setIsPreviewing(false);
        }

        const effectiveDeviceId = deviceIdOverride ?? audioDeviceId;

        try {
          const audioConstraints: MediaTrackConstraints | true = effectiveDeviceId
            ? { deviceId: { exact: effectiveDeviceId } }
            : true;
          const stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });

          // The permission prompt can outlive the component. Building the audio
          // pipeline after unmount leaves a live mic and an open AudioContext
          // that nothing will ever close — the mic indicator stays lit.
          if (isUnmountedRef.current) {
            stream.getTracks().forEach((t) => t.stop());
            return false;
          }

          setupAudioPipeline(stream);

          // Gracefully handle browser revoking mic (e.g., app backgrounded)
          const track = stream.getAudioTracks()[0];
          if (track) {
            track.onended = () => {
              if (!recorderRef.current) {
                cleanupStream();
                setIsPreviewing(false);
              }
            };
          }

          previewDeviceIdRef.current = effectiveDeviceId;
          setIsPreviewing(true);
          return true;
        } catch (err: unknown) {
          if (err instanceof DOMException && err.name === 'OverconstrainedError') {
            onStaleMic?.();
            try {
              const fallbackStream = await navigator.mediaDevices.getUserMedia({ audio: true });

              if (isUnmountedRef.current) {
                fallbackStream.getTracks().forEach((t) => t.stop());
                return false;
              }

              setupAudioPipeline(fallbackStream);
              previewDeviceIdRef.current = undefined;
              setIsPreviewing(true);
              return true;
            } catch (fallbackErr: unknown) {
              // Still can't get mic — report so the failure isn't invisible.
              errorReporter.expected('Audio preview fallback mic acquisition failed', fallbackErr, {
                feature: 'audio',
                operation: 'startPreview',
              });
            }
          }

          // Surface hard denials so the user knows why nothing happens when they
          // press record. CONTRACT #7 forbids falling through to startRecording()
          // here, so this is the only place the reason can reach the UI.
          // Transient failures (NotReadableError, AbortError — e.g. the mic is
          // still being released right after a reset) stay silent and are simply
          // retried by the next start attempt.
          if (err instanceof DOMException && err.name === 'NotAllowedError') {
            setError('Microphone access was denied. Please allow microphone permissions and try again.');
          } else if (err instanceof DOMException && err.name === 'NotFoundError') {
            setError('No microphone found. Please connect a microphone and try again.');
          }

          return false;
        }
      })();

      previewPromiseRef.current = promise;

      try {
        return await promise;
      } finally {
        previewStartingRef.current = false;
        previewPromiseRef.current = null;
      }
    },
    [audioDeviceId, onStaleMic, setupAudioPipeline, cleanupStream],
  );

  const stopPreview = useCallback(() => {
    cleanupStream();
    setIsPreviewing(false);
    previewDeviceIdRef.current = undefined;
  }, [cleanupStream]);

  const startRecording = useCallback(async () => {
    if (isRecording) return;

    setError(null);
    setIsPaused(false);
    setElapsed(0);
    elapsedRef.current = 0;
    chunksRef.current = [];
    setAudioLevels(new Array(WAVEFORM_BAR_COUNT).fill(0.05));

    // Reuse preview stream if it's live and on the correct device
    const existingStream = streamRef.current;
    const existingTrack = existingStream?.getAudioTracks()[0];
    const deviceIdMatches = !audioDeviceId || previewDeviceIdRef.current === audioDeviceId;

    if (existingStream && existingTrack && existingTrack.readyState === 'live' && deviceIdMatches) {
      setupAndStartRecording(existingStream);
      return;
    }

    // Preview stream not available or wrong device — acquire fresh
    if (existingStream) {
      cleanupStream();
    }

    try {
      const audioConstraints: MediaTrackConstraints | true = audioDeviceId
        ? { deviceId: { exact: audioDeviceId } }
        : true;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });

      if (isUnmountedRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      setupAndStartRecording(stream);
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        setError('Microphone access was denied. Please allow microphone permissions and try again.');
      } else if (err instanceof DOMException && err.name === 'NotFoundError') {
        setError('No microphone found. Please connect a microphone and try again.');
      } else if (err instanceof DOMException && err.name === 'OverconstrainedError') {
        try {
          localStorage.removeItem('notego_preferred_mic');
        } catch {
          /* noop */
        }
        onStaleMic?.();

        try {
          const fallbackStream = await navigator.mediaDevices.getUserMedia({ audio: true });

          if (isUnmountedRef.current) {
            fallbackStream.getTracks().forEach((t) => t.stop());
            return;
          }

          setupAndStartRecording(fallbackStream);
          return;
        } catch {
          setError(
            'Previously selected microphone is no longer available. Please select a different mic or try again.',
          );
        }
      } else {
        setError('Failed to start recording. Please try again.');
        errorReporter.expected('Failed to start audio recording', err, {
          feature: 'audio',
          operation: 'startRecording',
        });
      }
      cleanupStream();
    }
  }, [isRecording, audioDeviceId, onStaleMic, setupAndStartRecording, cleanupStream]);

  const pauseRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== 'recording') return;

    try {
      recorder.pause();
      pauseStartTimeRef.current = Date.now();
      setIsPaused(true);
      clearTimer();
      clearAnalyser();
    } catch (err: unknown) {
      errorReporter.expected('Failed to pause audio recording', err, {
        feature: 'audio',
        operation: 'pauseRecording',
      });
      setError('Failed to pause recording.');
    }
  }, [clearTimer, clearAnalyser]);

  const resumeRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== 'paused') return;

    try {
      recorder.resume();
      if (pauseStartTimeRef.current) {
        pausedDurationRef.current += Math.max(Date.now() - pauseStartTimeRef.current, 0);
      }
      pauseStartTimeRef.current = null;
      setIsPaused(false);
      startTimer();
      startWaveformUpdates();
    } catch (err: unknown) {
      errorReporter.expected('Failed to resume audio recording', err, {
        feature: 'audio',
        operation: 'resumeRecording',
      });
      setError('Failed to resume recording.');
    }
  }, [startTimer, startWaveformUpdates]);

  const stopRecording = useCallback(() => {
    if (!recorderRef.current) return;
    if (recorderRef.current.state !== 'inactive') {
      try {
        recorderRef.current.stop();
      } catch (err: unknown) {
        logger.warn('audio', 'Failed to stop MediaRecorder', err);
        setError('Failed to stop recording.');
        setIsRecording(false);
        setIsPaused(false);
        clearTimer();
        clearAnalyser();
        chunksRef.current = [];
        recorderRef.current = null;
        cleanupStream();
      }
    }
  }, [clearTimer, clearAnalyser, cleanupStream]);

  const resetRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.onstop = null;
      recorderRef.current.ondataavailable = null;
      recorderRef.current.stop();
    }
    cleanupStream();
    setIsRecording(false);
    setIsPaused(false);
    setElapsed(0);
    elapsedRef.current = 0;
    chunksRef.current = [];
    recorderRef.current = null;
    clearTimer();
    clearAnalyser();
    recordingStartTimeRef.current = null;
    pauseStartTimeRef.current = null;
    pausedDurationRef.current = 0;
    setAudioLevels(new Array(WAVEFORM_BAR_COUNT).fill(0.05));
    setIsPreviewing(false);
    // Do NOT clear previewStartingRef / previewPromiseRef here. They are the
    // in-flight dedupe guard that CONTRACT #7's fix relies on: clearing them
    // mid-flight lets a concurrent startPreview() issue a second getUserMedia,
    // orphaning one of the two streams. `startPreview` already clears both in
    // its own `finally`, which is the only place that knows the call finished.
    setError(null);
  }, [clearTimer, clearAnalyser, cleanupStream]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  useEffect(() => {
    isUnmountedRef.current = false;
    return () => {
      isUnmountedRef.current = true;
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.onstop = null;
        recorderRef.current.ondataavailable = null;
        recorderRef.current.stop();
      }
      if (timerRef.current) window.clearInterval(timerRef.current);
      if (analyserIntervalRef.current) window.clearInterval(analyserIntervalRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, []);

  return {
    isRecording,
    isPaused,
    isPreviewing,
    elapsed,
    audioLevels,
    analyserRef,
    error,
    startPreview,
    stopPreview,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    resetRecording,
    clearError,
  };
}
